import { sql } from "drizzle-orm";
import {
  check,
  index,
  integer,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { timestamps } from "@/db/columns.helpers";
import { activitySession } from "@/db/schema/activity-session";
import { user } from "@/db/schema/auth";
import type { MediaAssetSlim } from "@/lib/media";

// ──────────────────────────────────────────────────────────────────────────────
// SESSION SPLITS — "who owes what for this session"
//
// The ledger IS the product: it replaces the WhatsApp thread where an organizer
// works out the per-head number and chases people. No payment gateway is
// involved anywhere in this feature.
//
// TWO MODES:
//   • offline     — ledger only. The organizer marks rows settled after cash /
//                   UPI changes hands. Must work with zero Hiive Money.
//   • hiive_money — the worst-case share is BLOCKED from the participant's
//                   wallet at join (a normal credit hold, refType
//                   'activity_session_split_share') and settles automatically
//                   when the split closes. The block is what the organizer is
//                   buying: they never chase anyone.
//
// TWO PRICING TYPES, and they are different products wearing the same fields:
//   • divide_total      — "₹1,200 for the pitch, split among whoever plays."
//                         Per-head = total ÷ headcount and falls as people join.
//                         minParticipants is a PRICE CEILING: nobody can ever pay
//                         more than total ÷ min, because below min the session
//                         cancels. That ceiling is the figure we block.
//   • fixed_per_person  — "₹300 each." A coach, a clinic, a run club. The price
//                         never moves; minParticipants is a VIABILITY FLOOR with
//                         no bearing on price, and the block is just the price.
//
// AMOUNTS. The live per-head figure is computed from the roster and never
// persisted (see src/lib/activity-sessions/split.ts) — there is no stale row to
// reconcile. Only two amounts are stored per share, and they mean different
// things:
//   • blockedMinor — a real ledger position, written once at join.
//   • amountMinor  — the final charge, written at close.
// Their difference is what gets released back to the participant.
//
// EXTRAS. The cost of a game is not final when the whistle goes: somebody keeps
// ordering energy drinks. So closing is a 48-hour reconciliation window with
// itemised lines on the SAME split, and the organizer declares an extras
// ALLOWANCE at creation which does two things at once:
//   • sizes the block — blockedMinor = worstCaseShare × (1 + allowance)
//   • hard-caps what can ever be added, PER PERSON, summed across every line
// Base ≤ worst case and extras ≤ allowance, so the final amount can never
// exceed what was blocked. Not "usually" — never. That is the whole reason the
// ceiling is declared before anyone joins rather than picked afterwards: it
// converts an unbounded post-hoc liability into a bounded one somebody
// knowingly consented to and that we are holding money against.
// ──────────────────────────────────────────────────────────────────────────────

export const ACTIVITY_SESSION_SPLIT_MODE = ["offline", "hiive_money"] as const;
export type ActivitySessionSplitMode =
  (typeof ACTIVITY_SESSION_SPLIT_MODE)[number];

export const ACTIVITY_SESSION_SPLIT_PRICING = [
  "divide_total",
  "fixed_per_person",
] as const;
export type ActivitySessionSplitPricing =
  (typeof ACTIVITY_SESSION_SPLIT_PRICING)[number];

export const ACTIVITY_SESSION_SPLIT_STATUS = [
  "open", // Roster still moving, nothing charged
  "closed", // Final amounts fixed; Hiive Money already settled
  "voided", // Session cancelled / below minimum — every block reversed
] as const;
export type ActivitySessionSplitStatus =
  (typeof ACTIVITY_SESSION_SPLIT_STATUS)[number];

/**
 * How one individual row settles. Per-row rather than per-split so a friend with
 * no balance can sit inside a Hiive Money split as a visible exception (§5) —
 * there is deliberately no third "mixed" mode for the organizer to pick.
 */
export const ACTIVITY_SESSION_SHARE_SETTLEMENT_MODE = [
  "hiive_money",
  "offline",
] as const;
export type ActivitySessionShareSettlementMode =
  (typeof ACTIVITY_SESSION_SHARE_SETTLEMENT_MODE)[number];

/**
 * A participant asking to settle offline inside a Hiive Money split. Kept apart
 * from `settlementMode` on purpose: while the ask is pending the row is still a
 * normal hiive_money row, and a `declined` value has to survive so the same
 * person is not re-prompted every time they reopen the session.
 */
export const ACTIVITY_SESSION_SHARE_OFFLINE_REQUEST = [
  "requested",
  "allowed",
  "declined",
] as const;
export type ActivitySessionShareOfflineRequest =
  (typeof ACTIVITY_SESSION_SHARE_OFFLINE_REQUEST)[number];

export const ACTIVITY_SESSION_SHARE_STATUS = [
  "open", // Owes, nothing held (offline rows, and credit rows pre-block)
  "blocked", // Hiive Money hold placed, awaiting close
  "awaiting_confirmation", // Participant self-reported paying; organizer to confirm
  "settled", // Done — see settledVia for how
  "waived", // Organizer let it go
  "disputed", // Participant contests it; nudging and signals stop
] as const;
export type ActivitySessionShareStatus =
  (typeof ACTIVITY_SESSION_SHARE_STATUS)[number];

export const ACTIVITY_SESSION_SHARE_SETTLED_VIA = [
  "hiive_money",
  "offline",
  "mixed", // Part covered by a block, the remainder handed over directly (§5)
  "waived",
  "organizer", // The organizer's own row: they paid the venue, so it starts settled
] as const;
export type ActivitySessionShareSettledVia =
  (typeof ACTIVITY_SESSION_SHARE_SETTLED_VIA)[number];

export const ACTIVITY_SESSION_SHARE_DISPUTE_REASON = [
  "didnt_play",
  "wrong_amount",
  "already_paid",
  "other",
] as const;
export type ActivitySessionShareDisputeReason =
  (typeof ACTIVITY_SESSION_SHARE_DISPUTE_REASON)[number];

export const activitySessionSplit = sqliteTable(
  "activity_session_split",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    activitySessionId: integer("activity_session_id")
      .notNull()
      .references(() => activitySession.id, { onDelete: "cascade" }),

    // Session creator only — see §16.
    createdByUserId: text("created_by_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    mode: text("mode").notNull().$type<ActivitySessionSplitMode>(),

    // Immutable once anyone has joined: switching redefines what minimum and
    // maximum mean under people who already agreed to them (§8).
    pricing: text("pricing").notNull().$type<ActivitySessionSplitPricing>(),

    // Exactly one is set, matching `pricing`.
    totalAmountMinor: integer("total_amount_minor"),
    perPersonAmountMinor: integer("per_person_amount_minor"),

    // Whether the organizer counts in the divisor. Defaults false for
    // fixed_per_person — a coach is not one of the people paying.
    organizerPlaying: integer("organizer_playing", { mode: "boolean" })
      .notNull()
      .default(true),

    // ── Extras (§3) ──────────────────────────────────────────────────────────
    // Declared BEFORE anyone joins, and frozen at session start. Off means the
    // price is the price and nothing can be added later — the right setting for
    // a coach with a flat fee and no bar.
    extrasAllowed: integer("extras_allowed", { mode: "boolean" })
      .notNull()
      .default(false),

    /**
     * A ceiling as a percentage of the base, and it is enforced rather than
     * advised: it sizes the Hiive Money block AND caps what the organizer can
     * ever add, per person (§4). Frozen at session start, because a ceiling an
     * organizer could stretch once the tab arrived would bound nothing.
     */
    extrasAllowancePercent: integer("extras_allowance_percent")
      .notNull()
      .default(50),

    /** "drinks, extra hour" — free text, shown to everyone before they join. */
    extrasNote: text("extras_note"),

    status: text("status")
      .notNull()
      .$type<ActivitySessionSplitStatus>()
      .default("open"),

    closedAt: integer("closed_at", { mode: "timestamp" }),
    /** endTime + 48h. The reconciliation window auto-locks here. */
    closeDeadlineAt: integer("close_deadline_at", { mode: "timestamp" }),
    closedByUserId: text("closed_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),

    // ── Payment instructions (§6) ────────────────────────────────────────────
    // A SNAPSHOT of whichever saved entry the organizer picked, never a
    // reference to it: delete a preset six months later and a split people
    // still owe money on must not go blank. Same reasoning as the discount
    // snapshots elsewhere in this codebase.
    //
    // Deliberately generic. It is a file and some words, and what is in them is
    // the organizer's business — nothing here says UPI, so a payment link or
    // bank details need no migration.
    //
    // The label is load-bearing rather than decorative, because the payee is
    // not necessarily the organizer: handing out the VENUE's details so the
    // ground gets paid directly is a real and sensible thing organizers do, and
    // "Arjun — GPay" and "Turf Arena (the ground)" are very different things to
    // see on a payment screen.
    payeeLabel: text("payee_label"),
    payeeMedia: text("payee_media", {
      mode: "json",
    }).$type<MediaAssetSlim | null>(),
    payeeDetails: text("payee_details"),
    payeeNote: text("payee_note"),
    /** Provenance only — the copy above is the record. */
    payeePresetId: integer("payee_preset_id").references(
      () => userPaymentPreset.id,
      { onDelete: "set null" },
    ),

    /** Grant issued to the organizer at close, in hiive_money mode. */
    organizerGrantId: integer("organizer_grant_id"),

    currency: text("currency").notNull().default("INR"),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("activity_session_split_session_unique").on(
      t.activitySessionId,
    ),
    index("activity_session_split_creator_idx").on(t.createdByUserId),
    index("activity_session_split_status_idx").on(t.status),
    check(
      "activity_session_split_mode_valid",
      sql`${t.mode} IN ('offline', 'hiive_money')`,
    ),
    check(
      "activity_session_split_pricing_valid",
      sql`${t.pricing} IN ('divide_total', 'fixed_per_person')`,
    ),
    check(
      "activity_session_split_status_valid",
      sql`${t.status} IN ('open', 'closed', 'voided')`,
    ),
    check("activity_session_split_currency_inr", sql`${t.currency} = 'INR'`),
    check(
      "activity_session_split_extras_allowance_range",
      sql`${t.extrasAllowancePercent} BETWEEN 0 AND 100`,
    ),
    // Exactly one amount, and it matches the pricing type.
    check(
      "activity_session_split_amount_shape",
      sql`(${t.pricing} = 'divide_total' AND ${t.totalAmountMinor} > 0 AND ${t.perPersonAmountMinor} IS NULL)
        OR (${t.pricing} = 'fixed_per_person' AND ${t.perPersonAmountMinor} > 0 AND ${t.totalAmountMinor} IS NULL)`,
    ),
  ],
);

export const activitySessionSplitShare = sqliteTable(
  "activity_session_split_share",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    splitId: integer("split_id")
      .notNull()
      .references(() => activitySessionSplit.id, { onDelete: "cascade" }),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    /**
     * Final charge: base allocation plus every extras line this person is on.
     * NULL until close — before that it is computed live.
     */
    amountMinor: integer("amount_minor"),

    /**
     * What has actually arrived, split by route. A row is allowed to be part
     * paid from a block and part owed directly (§5). A payer-authored offline
     * claim is always the exact outstanding balance; this running total still
     * matters because a row can already be part-paid from a Hiive Money block,
     * and confirmed claims can later be reopened.
     *
     * `offlinePaidMinor` is derived from confirmed payment rows and persisted
     * anyway, deliberately against the usual runtime-computed preference: it is
     * money, it is read on every nudge and every reliability check, and a claim
     * can be rejected after the fact. Every write to a payment row recomputes
     * it in the same transaction.
     */
    creditPaidMinor: integer("credit_paid_minor").notNull().default(0),
    offlinePaidMinor: integer("offline_paid_minor").notNull().default(0),

    settlementMode: text("settlement_mode")
      .notNull()
      .$type<ActivitySessionShareSettlementMode>(),

    /** NULL = never asked. Survives a decline so we do not re-prompt. */
    offlineRequest:
      text("offline_request").$type<ActivitySessionShareOfflineRequest>(),
    offlineRequestedAt: integer("offline_requested_at", { mode: "timestamp" }),
    offlineDecidedAt: integer("offline_decided_at", { mode: "timestamp" }),

    status: text("status")
      .notNull()
      .$type<ActivitySessionShareStatus>()
      .default("open"),

    settledVia: text("settled_via").$type<ActivitySessionShareSettledVia>(),

    /** The real ledger position: worst case, written once at join. */
    blockedMinor: integer("blocked_minor"),

    settledAt: integer("settled_at", { mode: "timestamp" }),
    confirmedByUserId: text("confirmed_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    note: text("note"),

    /**
     * The terms this person last consented to, and a HARD CEILING on what they
     * can be charged: at close a share settles at the LOWER of the final
     * per-head and this. Only an explicit acceptance ever raises it. Since
     * non-responders come off the roster (§8) rather than being kept on old
     * terms, everyone still in the split has agreed to the current number and
     * the cap should never bind — which is exactly what makes it a safe
     * invariant rather than a policy.
     */
    agreedAmountMinor: integer("agreed_amount_minor"),

    /**
     * The other half of the terms: the most that can ever be added to this row
     * after the whistle, derived from the split's declared allowance at the
     * moment they consented.
     *
     * Kept as its own column rather than folded into `agreedAmountMinor`
     * because the two ceilings bind independently. Unticking no-shows at the
     * lock pushes the base per-head ABOVE the worst case, and it is
     * `agreedAmountMinor` that catches that; extras are capped by this. Their
     * sum is exactly `blockedMinor`, which is what makes
     * "final ≤ blocked" a local, checkable invariant rather than a property you
     * have to reason about across three files.
     */
    agreedExtrasMinor: integer("agreed_extras_minor").notNull().default(0),

    consentedAt: integer("consented_at", { mode: "timestamp" }),
    /** Set while a re-consent request is outstanding; cleared on response. */
    consentDeadlineAt: integer("consent_deadline_at", { mode: "timestamp" }),
    pendingAmountMinor: integer("pending_amount_minor"),

    disputeReason:
      text("dispute_reason").$type<ActivitySessionShareDisputeReason>(),
    disputeExtraId: integer("dispute_extra_id").references(
      () => activitySessionSplitExtra.id,
      { onDelete: "set null" },
    ),
    disputeNote: text("dispute_note"),
    disputedAt: integer("disputed_at", { mode: "timestamp" }),
    disputeEscalatedAt: integer("dispute_escalated_at", { mode: "timestamp" }),
    disputeResolvedAt: integer("dispute_resolved_at", { mode: "timestamp" }),
    /** Organizer pushed back once: "resolved, still owed". */
    disputePushedBackAt: integer("dispute_pushed_back_at", {
      mode: "timestamp",
    }),

    nudgeStage: integer("nudge_stage").notNull().default(0),
    lastNudgedAt: integer("last_nudged_at", { mode: "timestamp" }),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("activity_session_split_share_unique").on(t.splitId, t.userId),
    index("activity_session_split_share_split_idx").on(t.splitId),
    index("activity_session_split_share_user_idx").on(t.userId),
    index("activity_session_split_share_status_idx").on(t.status),
    // Nudge ladder and reliability signals scan open rows by user.
    index("activity_session_split_share_user_status_idx").on(
      t.userId,
      t.status,
    ),
    // Dispute queue: escalated Hiive Money rows, oldest first.
    index("activity_session_split_share_dispute_idx").on(
      t.disputedAt,
      t.disputeResolvedAt,
    ),
    check(
      "activity_session_split_share_settlement_mode_valid",
      sql`${t.settlementMode} IN ('hiive_money', 'offline')`,
    ),
    check(
      "activity_session_split_share_status_valid",
      sql`${t.status} IN ('open', 'blocked', 'awaiting_confirmation', 'settled', 'waived', 'disputed')`,
    ),
    check(
      "activity_session_split_share_settled_via_valid",
      sql`${t.settledVia} IS NULL OR ${t.settledVia} IN ('hiive_money', 'offline', 'mixed', 'waived', 'organizer')`,
    ),
    check(
      "activity_session_split_share_paid_non_negative",
      sql`${t.creditPaidMinor} >= 0 AND ${t.offlinePaidMinor} >= 0`,
    ),
    check(
      "activity_session_split_share_offline_request_valid",
      sql`${t.offlineRequest} IS NULL OR ${t.offlineRequest} IN ('requested', 'allowed', 'declined')`,
    ),
    check(
      "activity_session_split_share_dispute_reason_valid",
      sql`${t.disputeReason} IS NULL OR ${t.disputeReason} IN ('didnt_play', 'wrong_amount', 'already_paid', 'other')`,
    ),
  ],
);

// ──────────────────────────────────────────────────────────────────────────────
// EXTRAS — costs that only exist after the whistle (§4)
//
// Splitwise gets one thing exactly right and it is worth copying wholesale: an
// expense stores a PER-PERSON AMOUNT, and "split equally" is just a helper that
// fills those amounts in. Everything else — a subset of people, hand-entered
// amounts — is another helper writing to the same place.
//
// That matters here because it makes the common case and the awkward case the
// same feature rather than two. Adding drinks for everyone is one tap against
// the same table that carries "Rohan had four, I had a lime soda".
//
// Deliberately NOT modelled: "paid by". Splitwise's other axis turns one
// collector into an N×N debt graph with its own settle-up flow, and none of it
// can be automated by a block. Here there is exactly one collector, every extra
// is fronted by the organizer, and whoever actually bought the drinks squares
// up with them outside the app.
// ──────────────────────────────────────────────────────────────────────────────

/**
 * How the line was ENTERED. The per-person rows are the truth either way — this
 * only decides whether they get recomputed as the roster moves.
 */
export const ACTIVITY_SESSION_EXTRA_SPLIT_MODE = ["equally", "exact"] as const;
export type ActivitySessionExtraSplitMode =
  (typeof ACTIVITY_SESSION_EXTRA_SPLIT_MODE)[number];

export const activitySessionSplitExtra = sqliteTable(
  "activity_session_split_extra",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    splitId: integer("split_id")
      .notNull()
      .references(() => activitySessionSplit.id, { onDelete: "cascade" }),

    /** "Drinks", "Extra hour", "Shuttles". */
    label: text("label").notNull(),
    amountMinor: integer("amount_minor").notNull(),

    /** Optional, and REQUIRED once extras pass half the allowance (§4). */
    receiptMedia: text("receipt_media", {
      mode: "json",
    }).$type<MediaAssetSlim | null>(),

    splitMode: text("split_mode")
      .notNull()
      .$type<ActivitySessionExtraSplitMode>()
      .default("equally"),

    /**
     * Everyone-lines track the roster: add a late arrival at the lock and they
     * pick up their part automatically. Subset lines do not move — they were a
     * deliberate statement about who used the thing.
     */
    appliesToAll: integer("applies_to_all", { mode: "boolean" })
      .notNull()
      .default(true),

    addedByUserId: text("added_by_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    ...timestamps,
  },
  (t) => [
    index("activity_session_split_extra_split_idx").on(t.splitId),
    check(
      "activity_session_split_extra_amount_positive",
      sql`${t.amountMinor} > 0`,
    ),
    check(
      "activity_session_split_extra_split_mode_valid",
      sql`${t.splitMode} IN ('equally', 'exact')`,
    ),
  ],
);

/**
 * The Splitwise shape. One table serves everyone-lines, subset lines and
 * hand-entered amounts, so there is no second code path to get wrong.
 *
 * Keyed by userId rather than shareId on purpose: a share row is deleted and
 * recreated on a leave-and-rejoin, and a line item recorded before the game
 * should survive that.
 *
 * INVARIANT: SUM(amountMinor) == extra.amountMinor. Every line allocates in
 * full, using the same largest-remainder split as the base so the odd paise
 * do not hop between people when anything recomputes.
 */
export const activitySessionSplitExtraShare = sqliteTable(
  "activity_session_split_extra_share",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    extraId: integer("extra_id")
      .notNull()
      .references(() => activitySessionSplitExtra.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    amountMinor: integer("amount_minor").notNull(),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("activity_session_split_extra_share_unique").on(
      t.extraId,
      t.userId,
    ),
    index("activity_session_split_extra_share_user_idx").on(t.userId),
  ],
);

// ──────────────────────────────────────────────────────────────────────────────
// PAYMENT CLAIMS (§6)
//
// Marking thirty rows settled by hand is not a thing an organizer will do
// twice. So the person who actually knows a payment happened is the one who
// records it, and the organizer's job collapses to confirming.
//
// Append-only and amount-bearing rather than a boolean: the amount snapshots
// the exact outstanding balance claimed at that point in the audit trail, and
// the same rows cover a Hiive Money remainder. Claims are NEVER auto-confirmed
// — letting people clear their own debts unilaterally would make the ledger and
// every reliability signal worthless — but an open claim PAUSES nudging, so
// the cost of a slow organizer lands on the organizer rather than on somebody
// who has genuinely paid.
// ──────────────────────────────────────────────────────────────────────────────

/**
 * How the payer says they sent it. Descriptive metadata on a record, not a
 * mechanism we implement — without it the ledger drifts from reality and the
 * reliability signals become noise.
 */
export const ACTIVITY_SESSION_PAYMENT_METHOD = [
  "upi",
  "cash",
  "bank",
  "other",
] as const;
export type ActivitySessionPaymentMethod =
  (typeof ACTIVITY_SESSION_PAYMENT_METHOD)[number];

export const ACTIVITY_SESSION_PAYMENT_STATUS = [
  "claimed",
  "confirmed",
  "rejected",
] as const;
export type ActivitySessionPaymentStatus =
  (typeof ACTIVITY_SESSION_PAYMENT_STATUS)[number];

export const activitySessionSplitPayment = sqliteTable(
  "activity_session_split_payment",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    shareId: integer("share_id")
      .notNull()
      .references(() => activitySessionSplitShare.id, { onDelete: "cascade" }),

    claimedByUserId: text("claimed_by_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    amountMinor: integer("amount_minor").notNull(),
    method: text("method").notNull().$type<ActivitySessionPaymentMethod>(),
    note: text("note"),

    /**
     * The GPay screenshot people already send in the group chat. Visible ONLY
     * to the two people it is between — it is a bank app screenshot and can
     * carry a balance, a phone number or somebody's full name (§14).
     */
    proofMedia: text("proof_media", {
      mode: "json",
    }).$type<MediaAssetSlim | null>(),

    status: text("status")
      .notNull()
      .$type<ActivitySessionPaymentStatus>()
      .default("claimed"),

    confirmedByUserId: text("confirmed_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    confirmedAt: integer("confirmed_at", { mode: "timestamp" }),
    rejectedReason: text("rejected_reason"),

    /**
     * Set when the organizer un-confirms a payment they already accepted. The
     * claim itself is never deleted: it is an accusation about an event neither
     * party can prove, so both accounts stay on the record.
     */
    reopenedAt: integer("reopened_at", { mode: "timestamp" }),

    ...timestamps,
  },
  (t) => [
    index("activity_session_split_payment_share_idx").on(t.shareId, t.status),
    index("activity_session_split_payment_claimant_idx").on(t.claimedByUserId),
    check(
      "activity_session_split_payment_amount_positive",
      sql`${t.amountMinor} > 0`,
    ),
    check(
      "activity_session_split_payment_method_valid",
      sql`${t.method} IN ('upi', 'cash', 'bank', 'other')`,
    ),
    check(
      "activity_session_split_payment_status_valid",
      sql`${t.status} IN ('claimed', 'confirmed', 'rejected')`,
    ),
  ],
);

// ──────────────────────────────────────────────────────────────────────────────
// PAYMENT INSTRUCTION LIBRARY (§6)
//
// People do not have ONE of these. They have a personal handle, a second app, a
// business account for the sessions they coach, and the ground's own details
// for the pitch they book every Saturday. So the reusable thing lives on the
// profile and a split snapshots whichever one was picked.
//
// An attachment and some text. We display what the organizer attached and we do
// NOT verify it — no parsing, no test transfer, no rail. If they attach the
// wrong thing the money goes to the wrong place and we cannot retrieve it,
// which is the same position §7 already takes on offline money generally: we
// adjudicate what we custody, and we custody none of this.
// ──────────────────────────────────────────────────────────────────────────────

export const userPaymentPreset = sqliteTable(
  "user_payment_preset",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    /** "Arjun — GPay", "Turf Arena (the ground)". Shown to whoever is paying. */
    label: text("label").notNull(),
    media: text("media", { mode: "json" }).$type<MediaAssetSlim | null>(),
    details: text("details"),
    note: text("note"),

    /** Last-used is pre-selected, so the weekly game is zero taps. */
    lastUsedAt: integer("last_used_at", { mode: "timestamp" }),
    sortOrder: integer("sort_order").notNull().default(0),

    ...timestamps,
  },
  (t) => [
    index("user_payment_preset_user_idx").on(t.userId, t.lastUsedAt),
    // At least one of the two has to say something, or it is not instructions.
    check(
      "user_payment_preset_has_content",
      sql`${t.media} IS NOT NULL OR (${t.details} IS NOT NULL AND length(trim(${t.details})) > 0)`,
    ),
  ],
);

// ──────────────────────────────────────────────────────────────────────────────
// EDIT CONSENT
//
// Edits that can only help participants are free. Edits that can hurt them need
// re-consent. This is where a pending re-consent lives.
//
// It is deliberately a SESSION-level table rather than more columns on the
// share: a time or location change needs consent whether or not there is any
// money involved, and several fields changed in one sitting have to produce ONE
// request rather than five notifications.
//
// SILENCE MEANS OUT — one rule, whatever changed. At the deadline anyone who
// has not confirmed comes off the roster, their block is reversed, nothing is
// charged, and it costs them no reliability.
//
// The `kind` exists only so the COPY can differ: "your share goes from ₹200 to
// ₹240" and "we've moved to Saturday 9pm" are not the same sentence even though
// they end the same way.
//
// The alternative for money edits — keep non-responders on their old number and
// have the organizer absorb the gap — reads fair and does not survive contact
// with a group of friends. Nobody organising five-a-side eats ₹200 because two
// people did not check their phone; they would remove those people by hand,
// which they can already do. This is the same outcome with a deadline and a
// notification attached.
// ──────────────────────────────────────────────────────────────────────────────

export const ACTIVITY_SESSION_EDIT_KIND = [
  "money",
  "attendance",
  "both",
] as const;
export type ActivitySessionEditKind =
  (typeof ACTIVITY_SESSION_EDIT_KIND)[number];

export const ACTIVITY_SESSION_EDIT_STATUS = ["pending", "resolved"] as const;
export type ActivitySessionEditStatus =
  (typeof ACTIVITY_SESSION_EDIT_STATUS)[number];

export const ACTIVITY_SESSION_EDIT_DECISION = [
  "accepted",
  "declined",
  "expired",
] as const;
export type ActivitySessionEditDecision =
  (typeof ACTIVITY_SESSION_EDIT_DECISION)[number];

/** One field the organizer changed, as shown in the before/after. */
export type ActivitySessionEditChange = {
  field: string;
  label: string;
  from: string;
  to: string;
};

export const activitySessionEditRequest = sqliteTable(
  "activity_session_edit_request",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    activitySessionId: integer("activity_session_id")
      .notNull()
      .references(() => activitySession.id, { onDelete: "cascade" }),
    requestedByUserId: text("requested_by_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    kind: text("kind").notNull().$type<ActivitySessionEditKind>(),
    changes: text("changes", { mode: "json" })
      .notNull()
      .$type<ActivitySessionEditChange[]>(),
    /** Absolute, and never later than session start — the roster must be real by then. */
    deadlineAt: integer("deadline_at", { mode: "timestamp" }).notNull(),
    status: text("status")
      .notNull()
      .$type<ActivitySessionEditStatus>()
      .default("pending"),
    resolvedAt: integer("resolved_at", { mode: "timestamp" }),
    ...timestamps,
  },
  (t) => [
    index("activity_session_edit_request_session_idx").on(t.activitySessionId),
    index("activity_session_edit_request_status_idx").on(
      t.status,
      t.deadlineAt,
    ),
    check(
      "activity_session_edit_request_kind_valid",
      sql`${t.kind} IN ('money', 'attendance', 'both')`,
    ),
    check(
      "activity_session_edit_request_status_valid",
      sql`${t.status} IN ('pending', 'resolved')`,
    ),
  ],
);

export const activitySessionEditResponse = sqliteTable(
  "activity_session_edit_response",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    requestId: integer("request_id")
      .notNull()
      .references(() => activitySessionEditRequest.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    decision: text("decision").$type<ActivitySessionEditDecision>(),
    respondedAt: integer("responded_at", { mode: "timestamp" }),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("activity_session_edit_response_unique").on(
      t.requestId,
      t.userId,
    ),
    index("activity_session_edit_response_user_idx").on(t.userId),
    check(
      "activity_session_edit_response_decision_valid",
      sql`${t.decision} IS NULL OR ${t.decision} IN ('accepted', 'declined', 'expired')`,
    ),
  ],
);

export type ActivitySessionSplit = typeof activitySessionSplit.$inferSelect;
export type ActivitySessionSplitInsert =
  typeof activitySessionSplit.$inferInsert;
export type ActivitySessionSplitShare =
  typeof activitySessionSplitShare.$inferSelect;
export type ActivitySessionSplitShareInsert =
  typeof activitySessionSplitShare.$inferInsert;
export type ActivitySessionEditRequest =
  typeof activitySessionEditRequest.$inferSelect;
export type ActivitySessionEditResponse =
  typeof activitySessionEditResponse.$inferSelect;
export type ActivitySessionSplitExtra =
  typeof activitySessionSplitExtra.$inferSelect;
export type ActivitySessionSplitExtraShare =
  typeof activitySessionSplitExtraShare.$inferSelect;
export type ActivitySessionSplitPayment =
  typeof activitySessionSplitPayment.$inferSelect;
export type UserPaymentPreset = typeof userPaymentPreset.$inferSelect;
